/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { forwardRef, useState } from "react";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";

// custom styles for the NotificationItem
import { menuItem, menuImage } from "examples/Items/NotificationItem/styles";

const NotificationItem = forwardRef(({ color, image, title, date, onDelete, ...rest }, ref) => {
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    // show lid-open animation then call onDelete
    setDeleting(true);
    // small delay to show animation
    setTimeout(() => {
      if (onDelete) onDelete();
    }, 420);
  };

  return (
    <MenuItem {...rest} ref={ref} sx={(theme) => menuItem(theme)}>
      <VuiBox
        width="2.25rem"
        height="2.25rem"
        mt={0.25}
        mr={2}
        mb={0.25}
        borderRadius="lg"
        sx={(theme) => menuImage(theme, { color })}
      >
        {image}
      </VuiBox>
      <VuiBox sx={{ minWidth: 0, flex: 1 }}>
        <VuiTypography variant="button" textTransform="capitalize" fontWeight="regular" sx={{ color: "inherit" }}>
          <strong>{title[0]}</strong> {title[1]}
        </VuiTypography>
        <VuiTypography
          variant="caption"
          color="text"
          sx={{
            display: "flex",
            alignItems: "center",
            mt: 0.5,
          }}
        >
          <VuiTypography variant="button" color="text">
            <Icon
              sx={{
                lineHeight: 1.2,
                mr: 0.5,
              }}
            >
              watch_later
            </Icon>
          </VuiTypography>
          {date}
        </VuiTypography>
      </VuiBox>

      {/* Delete button on the right */}
      <VuiBox sx={{ ml: 1, display: "flex", alignItems: "center", height: "100%", flexShrink: 0 }}>
        <Tooltip title="Delete notification">
          <span>
            <IconButton
              size="small"
              onClick={handleDeleteClick}
              aria-label="Delete notification"
              sx={{
                color: deleting ? "#ff8a65" : "#e7f2ff",
                transform: deleting ? "rotate(-20deg) translateY(-4px)" : "none",
                transition: "transform 0.32s ease, color 0.2s",
              }}
            >
              <Icon sx={{ fontSize: 18 }}>{deleting ? "delete_forever" : "delete_outline"}</Icon>
            </IconButton>
          </span>
        </Tooltip>
      </VuiBox>
    </MenuItem>
  );
});

// Setting default values for the props of NotificationItem
NotificationItem.defaultProps = {
  color: "dark",
};

// Typechecking props for the NotificationItem
NotificationItem.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
    "text",
  ]),
  image: PropTypes.node.isRequired,
  title: PropTypes.arrayOf(PropTypes.string).isRequired,
  date: PropTypes.string.isRequired,
  onDelete: PropTypes.func,
};

export default NotificationItem;


